import React, { useState } from 'react';
import './ERDashboard.css';
import './TCOCDashboard.css';

const formatDecimal = (value) => (Number(value || 0)).toFixed(3);
const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

function TCOCBenchmark({ benchmarkData, setBenchmarkData }) {
  // inputValues stores string representations for smooth decimal typing (e.g. "1.")
  // benchmarkData (parent prop) is the single source of truth for numeric values
  const [inputValues, setInputValues] = useState(() => ({
    benchmarks: { ESRD: String(benchmarkData.benchmarks.ESRD), DIS: String(benchmarkData.benchmarks.DIS), AD: String(benchmarkData.benchmarks.AD), ND: String(benchmarkData.benchmarks.ND) },
    trends:     { ESRD: String(benchmarkData.trends.ESRD),     DIS: String(benchmarkData.trends.DIS),     AD: String(benchmarkData.trends.AD),     ND: String(benchmarkData.trends.ND) },
    riskScores: { ESRD: String(benchmarkData.riskScores.ESRD), DIS: String(benchmarkData.riskScores.DIS), AD: String(benchmarkData.riskScores.AD), ND: String(benchmarkData.riskScores.ND) }
  }));

  const handleInputChange = (category, cohort, value) => {
    // Update string display immediately (preserves "1." while typing decimals)
    setInputValues(prev => ({
      ...prev,
      [category]: { ...prev[category], [cohort]: value }
    }));

    // Parse to number and propagate to parent state immediately (no Save required)
    const numValue = value === '' ? 0 : (isNaN(parseFloat(value)) ? 0 : parseFloat(value));
    const updatedData = {
      ...benchmarkData,
      [category]: { ...benchmarkData[category], [cohort]: numValue }
    };
    setBenchmarkData(updatedData);
    localStorage.setItem('tcoc_benchmark_data', JSON.stringify(updatedData));
  };

  const handleSave = () => {
    localStorage.setItem('tcoc_benchmark_data', JSON.stringify(benchmarkData));
    alert('Benchmark data saved successfully!');
  };

  const handleReset = () => {
    const defaultData = {
      benchmarks: { ESRD: 0, DIS: 0, AD: 0, ND: 0 },
      trends: { ESRD: 1, DIS: 1, AD: 1, ND: 1 },
      riskScores: { ESRD: 0, DIS: 0, AD: 0, ND: 0 }
    };
    setBenchmarkData(defaultData);
    setInputValues({
      benchmarks: { ESRD: '0', DIS: '0', AD: '0', ND: '0' },
      trends:     { ESRD: '1', DIS: '1', AD: '1', ND: '1' },
      riskScores: { ESRD: '0', DIS: '0', AD: '0', ND: '0' }
    });
    localStorage.removeItem('tcoc_benchmark_data');
    alert('Benchmark data reset to defaults!');
  };

  const handleLoad = () => {
    const saved = localStorage.getItem('tcoc_benchmark_data');
    if (saved) {
      try {
        const loadedData = JSON.parse(saved);
        setBenchmarkData(loadedData);
        setInputValues({
          benchmarks: { ESRD: String(loadedData.benchmarks.ESRD), DIS: String(loadedData.benchmarks.DIS), AD: String(loadedData.benchmarks.AD), ND: String(loadedData.benchmarks.ND) },
          trends:     { ESRD: String(loadedData.trends.ESRD),     DIS: String(loadedData.trends.DIS),     AD: String(loadedData.trends.AD),     ND: String(loadedData.trends.ND) },
          riskScores: { ESRD: String(loadedData.riskScores.ESRD), DIS: String(loadedData.riskScores.DIS), AD: String(loadedData.riskScores.AD), ND: String(loadedData.riskScores.ND) }
        });
        alert('Benchmark data loaded from saved state!');
      } catch (e) {
        alert('Error loading saved data.');
      }
    } else {
      alert('No saved benchmark data found.');
    }
  };

  return (
    <div style={{ width: '100%', margin: 0, padding: '20px', backgroundColor: '#f3f4f6', minHeight: '100vh', boxSizing: 'border-box' }}>
      <h1 className="dashboard-title">Benchmark Input</h1>
      <p style={{ marginBottom: '30px', color: '#6b7280', fontSize: '14px' }}>
        Enter the benchmark values, trends, and BY3 risk scores for each cohort. 
        These values will be used to calculate savings and MLR in the PY General and PY Deep Dive dashboards.
      </p>

      {/* Benchmark Values */}
      <div className="table-section">
          <h2>Benchmark PMPY Values</h2>
          <p className="table-description">
            Enter the benchmark PMPY values from the CMS benchmark report for each enrollment type.
            Default value is $0.
          </p>
          <table className="summary-table benchmark-table">
            <thead>
              <tr>
                <th>Cohort</th>
                <th>Benchmark PMPY ($)</th>
              </tr>
            </thead>
            <tbody>
              {['ESRD', 'DIS', 'AD', 'ND'].map(cohort => (
                <tr key={cohort}>
                  <td><strong>{cohort}</strong></td>
                  <td>
                    <input
                      type="text"
                      value={inputValues.benchmarks[cohort]}
                      onChange={(e) => handleInputChange('benchmarks', cohort, e.target.value)}
                      className="benchmark-input"
                      placeholder="0.00"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Trend Values */}
        <div className="table-section">
          <h2>Trend Values (BY3 to PY)</h2>
          <p className="table-description">
            Enter the national/regional trend factor from BY3 to PY for each enrollment type.
            Default value is 1.000 (no trend). Display with 3 decimal places.
          </p>
          <table className="summary-table benchmark-table">
            <thead>
              <tr>
                <th>Cohort</th>
                <th>Trend Factor</th>
              </tr>
            </thead>
            <tbody>
              {['ESRD', 'DIS', 'AD', 'ND'].map(cohort => (
                <tr key={cohort}>
                  <td><strong>{cohort}</strong></td>
                  <td>
                    <input
                      type="text"
                      value={inputValues.trends[cohort]}
                      onChange={(e) => handleInputChange('trends', cohort, e.target.value)}
                      className="benchmark-input"
                      placeholder="1.000"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* BY3 Risk Scores */}
        <div className="table-section">
          <h2>BY3 Risk Scores</h2>
          <p className="table-description">
            Enter the BY3 (Benchmark Year 3) risk scores for each enrollment type.
            These are used to calculate risk trend adjustments. Default value is 0.000. Display with 3 decimal places.
          </p>
          <table className="summary-table benchmark-table">
            <thead>
              <tr>
                <th>Cohort</th>
                <th>BY3 Risk Score</th>
              </tr>
            </thead>
            <tbody>
              {['ESRD', 'DIS', 'AD', 'ND'].map(cohort => (
                <tr key={cohort}>
                  <td><strong>{cohort}</strong></td>
                  <td>
                    <input
                      type="text"
                      value={inputValues.riskScores[cohort]}
                      onChange={(e) => handleInputChange('riskScores', cohort, e.target.value)}
                      className="benchmark-input"
                      placeholder="0.000"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="benchmark-actions" style={{ marginTop: '30px', textAlign: 'center' }}>
          <button 
            onClick={handleSave} 
            className="toggle-btn"
            style={{ 
              backgroundColor: '#4caf50', 
              color: 'white',
              border: 'none'
            }}
          >
            Save to Browser
          </button>
          <button onClick={handleLoad} className="toggle-btn" style={{ backgroundColor: '#6c757d', color: 'white', border: 'none' }}>
            Load Saved Data
          </button>
          <button onClick={handleReset} className="toggle-btn" style={{ backgroundColor: '#dc3545', color: 'white', border: 'none' }}>
            Reset to Defaults
          </button>
        </div>

        {/* Summary Display */}
        <div className="summary-table-section" style={{ marginTop: '40px' }}>
          <h2>Current Values Summary</h2>
          <table className="summary-table">
            <thead>
              <tr>
                <th>Cohort</th>
                <th>Benchmark PMPY</th>
                <th>Trend</th>
                <th>BY3 Risk Score</th>
              </tr>
            </thead>
            <tbody>
              {['ESRD', 'DIS', 'AD', 'ND'].map(cohort => (
                <tr key={cohort}>
                  <td><strong>{cohort}</strong></td>
                  <td>{formatCurrency(benchmarkData.benchmarks[cohort])}</td>
                  <td>{formatDecimal(benchmarkData.trends[cohort])}</td>
                  <td>{formatDecimal(benchmarkData.riskScores[cohort])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Help Section */}
        <div className="summary-table-section" style={{ backgroundColor: '#f8f9fa', marginTop: '30px' }}>
          <h3>How to Use This Page</h3>
          <ul>
            <li><strong>Benchmarks:</strong> Enter the benchmark PMPY values from your CMS benchmark report for each enrollment type (ESRD, DIS, AD, ND).</li>
            <li><strong>Trends:</strong> Enter the national/regional trend factor from BY3 to PY. This is typically close to 1.0.</li>
            <li><strong>BY3 Risk Scores:</strong> Enter the risk scores from Benchmark Year 3 for each enrollment type. These are used to calculate risk adjustments.</li>
            <li><strong>Save to Browser:</strong> Explicitly saves the current values to your browser's local storage for persistence across sessions.</li>
            <li><strong>Load Saved Data:</strong> Restore previously saved benchmark data from your browser's local storage.</li>
            <li><strong>Reset to Defaults:</strong> Clear all values and return to default settings (0 for benchmarks/risk scores, 1 for trends).</li>
          </ul>
          <p style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e7f3ff', borderLeft: '4px solid #007bff', borderRadius: '4px' }}>
          <strong>Note:</strong> Values update the PY Deep Dive and PY General dashboards <strong>immediately as you type</strong> — no Save button required. 
            Use "Save to Browser" to persist values across browser sessions (local storage).
          </p>
        </div>
    </div>
  );
}

export default TCOCBenchmark;
